// AiSpecPage — 公开文档页 /ai/spec
//
// 列出当前 AI 规范系统支持的所有 canonical 模型，给用户/开发者一眼看出：
//   - 我们标准化的模型 id 是什么（"gpt-4-6"、"claude-sonnet-4-7"、…）
//   - 每个模型的 vendor / family / 输入输出模态 / context_length / 能力
//   - 有几个 provider 在提供（providerCount > 0 才是真能调）
//   - 怎么用 OpenAI SDK 接入（base URL + 示例 curl）
//
// 数据来源：GET /v1/ai-spec/canonical-models（公开端点）
//
// 设计：纯静态展示，无交互。后续 plan 接入：
//   - 单 canonical 详情页（/ai/spec/models/:id）展示提供的 provider 列表
//   - playground（输入 prompt 试调）

import React, { useEffect as uE, useMemo as uM, useState as uS } from 'react';
import { XShell } from '../shell.jsx';
import { XApi } from '../services.js';
import { XPrim } from '../primitives.jsx';
import { listCanonicalModelsPublic, listProvidersForCanonical } from '../api/ai-spec.js';

// ── 标题 + 副标题 ──────────────────────────────────────────────────────────
const Header = () => (
  <section className="sec-pad-tight section-seam">
    <div style={{ maxWidth: 1480, margin: '0 auto', padding: '0 40px' }}>
      <div className="sec-num"><span className="grey">/</span>&nbsp;AI SPEC</div>
      <h1
        className="gs-section-title"
        style={{
          margin: '14px 0 10px',
          fontSize: 'clamp(30px, 4vw, 46px)',
          letterSpacing: '-0.035em',
          fontWeight: 600,
          lineHeight: 1.05,
        }}
      >
        Canonical Models
      </h1>
      <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 14, lineHeight: 1.65, maxWidth: 760 }}>
        我们对各家 AI 模型做了标准命名（如 <code>gpt-5-4</code>、
        <code>claude-sonnet-4-6</code>），让你用一套统一的协议调到任意上游。
        所有模型都通过 <code>POST /v1/chat/completions</code> 暴露，
        客户端用任意 OpenAI 兼容 SDK 即可调用。
      </p>
    </div>
  </section>
);

// ── How-to 示例 ────────────────────────────────────────────────────────────
const HowTo = ({ apiBase }) => {
  const base = apiBase || 'https://ai.xapi.to';
  return (
    <section className="sec-pad-tight section-seam">
      <div style={{ maxWidth: 1480, margin: '0 auto', padding: '0 40px' }}>
        <div className="sec-num"><span className="grey">/</span>&nbsp;HOW TO USE</div>
        <h2 style={{ fontSize: 22, margin: '12px 0 16px', fontWeight: 600, letterSpacing: '-0.02em' }}>
          Quick start
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
          <pre style={codeBlockStyle}>{`# curl
curl -X POST ${base}/v1/chat/completions \\
  -H 'Content-Type: application/json' \\
  -d '{
    "model": "gpt-5-4",
    "messages": [{"role": "user", "content": "Hello"}],
    "max_tokens": 100
  }'`}</pre>
          <pre style={codeBlockStyle}>{`# OpenAI Python SDK
from openai import OpenAI

client = OpenAI(
    base_url="${base}/v1",
    api_key="sk-xapi-..."
)

resp = client.chat.completions.create(
    model="gpt-5-4",
    messages=[{"role": "user", "content": "Hello"}],
)`}</pre>
        </div>
      </div>
    </section>
  );
};

const codeBlockStyle = {
  background: 'var(--bg-1)',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '14px 16px',
  fontSize: 12.5,
  lineHeight: 1.55,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  color: 'var(--fg-2)',
  overflow: 'auto',
  margin: 0,
};

// ── 模态徽章 ───────────────────────────────────────────────────────────────
const ModalityBadge = ({ kind, items }) => (
  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11.5 }}>
    <span style={{ color: 'var(--muted, #888)' }}>{kind}:</span>
    {items.map((m) => (
      <span
        key={m}
        style={{
          padding: '1px 6px',
          borderRadius: 4,
          background: 'rgba(0,150,200,0.08)',
          border: '1px solid rgba(0,150,200,0.20)',
          fontSize: 11,
        }}
      >
        {m}
      </span>
    ))}
  </span>
);

// ── 能力标签 ───────────────────────────────────────────────────────────────
const Cap = ({ label, on }) =>
  on ? (
    <span
      style={{
        fontSize: 11,
        padding: '1px 6px',
        borderRadius: 4,
        background: 'rgba(0,180,80,0.08)',
        border: '1px solid rgba(0,180,80,0.20)',
        color: 'rgb(0,120,55)',
      }}
    >
      {label}
    </span>
  ) : null;

// ── 单个模型卡片 ───────────────────────────────────────────────────────────
const ModelCard = ({ m, onShowProviders }) => {
  const isAvail = m.providerCount > 0;
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 14,
        background: 'var(--bg-1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        opacity: isAvail ? 1 : 0.55,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <code
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {m.id}
          </code>
          <div style={{ fontSize: 12, color: 'var(--muted, #888)', marginTop: 2 }}>
            {m.vendor} · {m.family} · {m.status}
          </div>
        </div>
        <button
          type="button"
          disabled={!isAvail}
          onClick={() => isAvail && onShowProviders && onShowProviders(m)}
          title={isAvail ? 'Click to see which services offer this model' : 'No active provider'}
          style={{
            fontSize: 11,
            padding: '2px 6px',
            borderRadius: 4,
            background: isAvail ? 'rgba(0,180,80,0.10)' : 'rgba(180,180,180,0.10)',
            border: `1px solid ${isAvail ? 'rgba(0,180,80,0.25)' : 'rgba(180,180,180,0.30)'}`,
            color: isAvail ? 'rgb(0,120,55)' : 'rgb(120,120,120)',
            whiteSpace: 'nowrap',
            cursor: isAvail ? 'pointer' : 'default',
          }}
        >
          {isAvail ? `${m.providerCount} provider${m.providerCount > 1 ? 's' : ''} →` : 'unavailable'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <ModalityBadge kind="in" items={m.modality.input} />
        <ModalityBadge kind="out" items={m.modality.output} />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {m.capabilities.context_length > 0 && (
          <span style={{ fontSize: 11.5, color: 'var(--muted, #888)' }}>
            context {formatTokens(m.capabilities.context_length)}
          </span>
        )}
        {m.capabilities.max_output_tokens && (
          <span style={{ fontSize: 11.5, color: 'var(--muted, #888)' }}>
            · out {formatTokens(m.capabilities.max_output_tokens)}
          </span>
        )}
        <Cap label="streaming" on={m.capabilities.supports_streaming} />
        <Cap label="tools" on={m.capabilities.supports_tools} />
        <Cap label="thinking" on={m.capabilities.supports_thinking} />
      </div>

      {m.aliases && m.aliases.length > 0 && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 11.5, color: 'var(--muted, #888)', cursor: 'pointer' }}>
            {m.aliases.length} alias{m.aliases.length > 1 ? 'es' : ''}
          </summary>
          <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--muted, #888)', fontFamily: 'monospace' }}>
            {m.aliases.join(', ')}
          </div>
        </details>
      )}
    </div>
  );
};

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

// ── 模型列表（按 vendor 分组）──────────────────────────────────────────────
const ModelList = ({ models, onShowProviders, filterUI }) => {
  const grouped = uM(() => {
    const map = new Map();
    for (const m of models) {
      const key = m.vendor;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [models]);

  return (
    <section className="sec-pad-tight section-seam">
      <div style={{ maxWidth: 1480, margin: '0 auto', padding: '0 40px' }}>
        <div className="sec-num"><span className="grey">/</span>&nbsp;MODELS</div>
        <h2 style={{ fontSize: 22, margin: '12px 0 16px', fontWeight: 600, letterSpacing: '-0.02em' }}>
          {models.length} canonical model{models.length === 1 ? '' : 's'}
        </h2>
        {filterUI}
        {grouped.map(([vendor, list]) => (
          <div key={vendor} style={{ marginBottom: 28 }}>
            <h3
              style={{
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--muted, #888)',
                marginBottom: 12,
                fontWeight: 600,
              }}
            >
              {vendor} ({list.length})
            </h3>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              }}
            >
              {list.map((m) => (
                <ModelCard key={m.id} m={m} onShowProviders={onShowProviders} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── C1: ProvidersDialog — 显示哪些 ApiService 提供这个 canonical ─────────
const ProvidersDialog = ({ model, onClose }) => {
  const [providers, setProviders] = uS(null);
  const [err, setErr] = uS(null);

  uE(() => {
    if (!model) return;
    let cancelled = false;
    setProviders(null);
    setErr(null);
    listProvidersForCanonical(model.id)
      .then((d) => !cancelled && setProviders(d))
      .catch((e) => !cancelled && setErr(e.message || String(e)));
    return () => { cancelled = true; };
  }, [model]);

  if (!model) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.40)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8, minWidth: 480, maxWidth: 720, maxHeight: '80vh',
          overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.20)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 11, color: 'var(--muted, #888)', letterSpacing: '0.05em' }}>PROVIDERS FOR</div>
          <code style={{ fontSize: 16, fontWeight: 600 }}>{model.id}</code>
          <div style={{ fontSize: 12, color: 'var(--muted, #888)', marginTop: 2 }}>
            {model.vendor} · {model.family}
          </div>
        </div>
        <div style={{ padding: 16 }}>
          {err ? (
            <div style={{ color: 'rgb(160,30,30)', fontSize: 13 }}>{err}</div>
          ) : providers === null ? (
            <div style={{ color: 'var(--muted, #888)' }}>Loading…</div>
          ) : providers.length === 0 ? (
            <div style={{ color: 'var(--muted, #888)', fontSize: 13 }}>
              No active service provides this canonical model yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {providers.map((p, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '10px 12px', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13.5 }}>{p.apiServiceName}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted, #888)', fontFamily: 'monospace', marginTop: 2 }}>
                      {p.endpointPath} · <span style={{ color: 'rgb(0,90,140)' }}>{p.interfaceSpecId}</span>
                      {' · upstream: '}<code>{p.upstreamModelName}</code>
                    </div>
                  </div>
                  {p.category && (
                    <span style={{ fontSize: 11, color: 'var(--muted, #aaa)' }}>{p.category}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(0,0,0,0.06)', textAlign: 'right' }}>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Filter pills（available / all / unavailable）─────────────────────────────
// 默认 "available" — 只有 providerCount > 0 的模型才能真正路由调到。
// "all" — 含 catalog-only 的全量目录（方便服务商看自己未来要支持哪些）。
// "unavailable" — 只看没人提供的，等服务商接入。
const FilterPills = ({ value, onChange, counts }) => {
  const opts = [
    { k: 'available',   l: 'Available',   n: counts.available },
    { k: 'all',         l: 'All catalog', n: counts.all },
    { k: 'unavailable', l: 'Unavailable', n: counts.unavailable },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
      {opts.map((o) => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          style={{
            padding: '5px 12px',
            borderRadius: 14,
            fontSize: 12.5,
            cursor: 'pointer',
            border: '1px solid',
            borderColor: value === o.k ? 'rgb(0,90,140)' : 'rgba(0,0,0,0.12)',
            background: value === o.k ? 'rgba(0,150,200,0.08)' : 'transparent',
            color: value === o.k ? 'rgb(0,90,140)' : 'inherit',
            fontWeight: value === o.k ? 600 : 400,
          }}
        >
          {o.l} <span style={{ opacity: 0.6, fontWeight: 400 }}>({o.n})</span>
        </button>
      ))}
    </div>
  );
};

// ── 主页面 ─────────────────────────────────────────────────────────────────
export const AiSpecPage = () => {
  const [models, setModels] = uS(null);
  const [err, setErr] = uS(null);
  const [drillModel, setDrillModel] = uS(null); // C1
  const [filter, setFilter] = uS('available'); // 'available' | 'all' | 'unavailable'

  uE(() => {
    let cancelled = false;
    listCanonicalModelsPublic()
      .then((data) => {
        if (!cancelled) setModels(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e.message || String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = uM(() => {
    if (!models) return { available: 0, all: 0, unavailable: 0 };
    const avail = models.filter((m) => m.providerCount > 0).length;
    return { available: avail, all: models.length, unavailable: models.length - avail };
  }, [models]);

  const filtered = uM(() => {
    if (!models) return null;
    if (filter === 'available') return models.filter((m) => m.providerCount > 0);
    if (filter === 'unavailable') return models.filter((m) => m.providerCount === 0);
    return models;
  }, [models, filter]);

  const apiBase = XApi?.config?.apiBase
    ? // apiBase 通常是 "/api" 这种相对路径——拼成完整 URL 给文档
      (XApi.config.apiBase.startsWith('http')
        ? XApi.config.apiBase
        : window.location.origin + XApi.config.apiBase)
    : window.location.origin + '/api';

  return (
    <div>
      <Header />
      <HowTo apiBase={apiBase} />
      {err ? (
        <section className="sec-pad-tight">
          <div style={{ maxWidth: 1480, margin: '0 auto', padding: '0 40px' }}>
            <div
              style={{
                padding: 14,
                border: '1px solid rgba(200,50,50,0.25)',
                background: 'rgba(200,50,50,0.05)',
                color: 'rgb(160,30,30)',
                borderRadius: 6,
              }}
            >
              <strong>Failed to load models:</strong> {err}
            </div>
          </div>
        </section>
      ) : models === null ? (
        <section className="sec-pad-tight">
          <div style={{ maxWidth: 1480, margin: '0 auto', padding: '0 40px' }}>
            <div style={{ color: 'var(--muted, #888)', fontSize: 14 }}>Loading…</div>
          </div>
        </section>
      ) : (
        <ModelList
          models={filtered}
          onShowProviders={setDrillModel}
          filterUI={<FilterPills value={filter} onChange={setFilter} counts={counts} />}
        />
      )}
      <ProvidersDialog model={drillModel} onClose={() => setDrillModel(null)} />
    </div>
  );
};

export default AiSpecPage;
